import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Button, Type} from '@Components/button';
import {getPosterPreviewURL, PosterPreviewSize} from '@Libraries/poster-library';
import styles from '@Panels/poster-publishing-options-panel/components/publishing-share-to-app-panel/publishing-share-to-app-panel.module.scss';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {getUserPremiumLevel} from '@Libraries/user.library';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch} from '@Components/toggle-switch-v2';
import {useFreebieProduct, useHashedId, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {GA4EventParamName} from '@Libraries/ga-events';
import {ANALYTICS_ACTION_FOR_PUBLISH} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {getAssetUrl} from '@Utils/s3.util';
import {useAppDispatch} from '@/hooks';
import type {AJAXisHighResResponse, AJAXPollingResponseForPreparingDesign, PublishingShareToAppPanelProps} from './publishing-share-to-app-panel.types';

export function PublishingShareToAppPanel({...props}: PublishingShareToAppPanelProps): ReactElement {
  const [isLoading, setIsLoading] = useState(true);
  const [isHighRes, setIsHighRes] = useState(0);
  const [idProduct, setIdProduct] = useState(0);
  const freebieProduct = useFreebieProduct();
  const printProduct = usePrintProduct();
  const [removeWatermark, setRemoveWatermark] = useState(false);
  const productUsed = printProduct?.needsPurchase ? freebieProduct : printProduct;
  const posterHashedId = useHashedId();
  const image: string = getPosterPreviewURL(posterHashedId, false, PosterPreviewSize.THUMB);
  const [fileToShare, setFileToShare] = useState<File>(new File([], ''));
  const [canShare, setCanShare] = useState(false);
  const dispatch = useAppDispatch();
  const orderId = printProduct?.orderId;
  const status = printProduct?.status;
  window.PMW.gtm.trackGA4CustomEvent(ANALYTICS_ACTION_FOR_PUBLISH, {[GA4EventParamName.TYPE]: 'ShareToApp'});

  const isShareToAppAvailable = (): any => {
    return window.PMW.isMobileOS() && typeof navigator !== 'undefined' && navigator.canShare.bind(navigator);
  };
  const getOrderDetails = (): void => {
    if (isShareToAppAvailable()) {
      void window.PMW.readLocal('socialmedia/canUserShareHighRes', {hashedId: posterHashedId}).then((response: AJAXisHighResResponse) => {
        if (response) {
          setIdProduct(Number(response.idproduct));
          setIsHighRes(response.highres ? 1 : 0);
        }
      });
    }
  };

  const initFooter = (): void => {
    setIsLoading(true);
    if (!removeWatermark && getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG) {
      void window.PMW.pollingAjaxCallAsync(doPrepareWebShareDesign, webShareDownloadReady.bind(null, 1))
        .then((response: AJAXPollingResponseForPreparingDesign) => {
          onGenerationComplete(response);
          setCanShare(true);
        })
        .catch(() => {
          onWebShareError.bind(null);
        });
    } else {
      if (Number(orderId) !== 0 && window.PMW.getUserPremiumLevel() === USER_PREMIUM_LEVELS.PAYG) {
        void window.PMW.writeLocal('cart/useFreeCorrectionNew', {
          idorder: orderId,
        })
          .then(() => {
            doWebShare();
          })
          .catch(() => {
            dispatch(setErrorOnPublishPanel(true));
            dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
          });
      } else {
        void window.PMW.writeLocal('cart/checkoutpublish', {
          idposter: posterHashedId,
          idproduct: idProduct,
          promotion: '',
          mode: 'share-to-app',
        })
          .then((response) => {
            if (response.orderRegen) {
              doWebShare();
              return;
            }
            parseResponseForCheckout(response);
          })
          .catch(() => {
            dispatch(setErrorOnPublishPanel(true));
            dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
          });
      }
    }
  };
  const parseResponseForCheckout = (response: {orderRegen: number; status: string; checkout: {[x: string]: number}; message: string | any[]}): void => {
    const STATUS_DOWNLOADABLE = 'downloadable';
    if (typeof response.orderRegen !== 'undefined' && response.orderRegen === 1) {
      doWebShare();
      return;
    }
    if (
      typeof response.status !== 'undefined' &&
      (response.status === 'new-order' || response.status === 'credit-usable') &&
      typeof response.checkout !== 'undefined' &&
      response.checkout['raw-price'] === 0
    ) {
      void window.PMW.writeLocal('cart/newPremiumDownload/', {
        id: idProduct,
        hid: posterHashedId,
        is: 1,
      })
        .then(() => {
          doWebShare();
        })
        .catch(() => {
          dispatch(setErrorOnPublishPanel(true));
          dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
        });
    } else if (typeof response.status !== 'undefined' && response.status === STATUS_DOWNLOADABLE) {
      doWebShare();
    } else if (typeof response.message !== 'undefined' && response.message.length !== 0) {
      onWebShareError();
      //   dispatch things and show error and redirect to main page
    } else if (typeof response.checkout['raw-price'] !== 'undefined' && response.checkout['raw-price'] > 0) {
      if (window.PMW.enableCheckoutV2) {
        window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
          window.PMW.postCheckoutProcessing(response, null, () => {
            doWebShare();
          });
        });
      } else {
        window.PMW.postCheckoutProcessing(response, null, () => {
          doWebShare();
        });
      }
    } else {
      doWebShare();
    }
  };

  const doWebShare = (): void => {
    setIsLoading(true);
    window.PMW.pollingAjaxCallAsync(doPrepareWebShareDesign, webShareDownloadReady.bind(null, 0))
      .then((response: AJAXPollingResponseForPreparingDesign) => {
        // Handle the response if needed
        setCanShare(true);
        onGenerationComplete(response);
      })
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
        onWebShareError.bind(null);
      });
  };

  const onGenerationComplete = (data: AJAXPollingResponseForPreparingDesign): void => {
    if (data.url) {
      createBase64FromURL(data.url);
    }
  };
  const webShareDownloadReady = (isFreebie: number): Promise<any> => {
    return window.PMW.readLocal('socialmedia/isWebShareDownloadReady', {
      hashedId: posterHashedId,
      isFreebie,
    });
  };
  const doPrepareWebShareDesign = (): Promise<any> => {
    return window.PMW.writeLocal('socialmedia/prepareDesignForWebShare', {
      hashedId: posterHashedId,
      isHighres: isHighRes,
    });
  };

  const createBase64FromURL = (url: string): void => {
    const xhr: XMLHttpRequest = new XMLHttpRequest();
    let hasError = false;

    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    xhr.onload = function onLoad(): void {
      if (!hasError) {
        const blob: Blob = xhr.response as Blob;
        const fileName = `${productUsed?.posterName || ''}.${productUsed?.format || ''}`;
        const file = new File([blob], fileName, {
          type: productUsed?.mimeType,
        });
        setFileToShare(file);
        setIsLoading(false);
      }
    };

    xhr.onreadystatechange = function onReadyStateChange(): void {
      if (xhr.readyState === 4) {
        if (xhr.status >= 400) {
          hasError = true;
          onWebShareError();
        }
      }
    };

    xhr.onerror = (): void => {
      return onWebShareError();
    };

    window.PMW.addAjaxParamsForSubdomainToSubfolderRequests(xhr, true);
    xhr.send();
  };

  const onWebShareError = (): void => {
    dispatch(setErrorOnPublishPanel(true));
  };

  const onShareToApp = (): void => {
    let title = `PosterMyWall | ${productUsed?.posterName}`;
    let text = window.i18next.t('pmwjs_made_with_postermywall');

    /**
     * @see https://stackoverflow.com/questions/60302475/can-i-share-a-file-with-web-share-api-on-safari/66126240#66126240
     * Apparently, text/title needs to be empty for sharing assets through iOS
     */
    if (window.PMW.isIOS()) {
      title = '';
      text = '';
    }

    void navigator
      .share({
        files: [fileToShare],
        title,
        text,
      })
      .catch(() => {
        setIsLoading(false);
        // Not showing error here since simply closing the share dialog will also cause this
      });
  };

  useEffect(() => {
    setIsLoading(true);
    if (window.PMW.isPosterMaker()) {
      void window.PMW.writeLocal('poster/getPosterDataForSharingToApp/', {
        hid: posterHashedId,
      })
        .then(() => {
          getOrderDetails();
        })
        .catch(() => {
          dispatch(setErrorOnPublishPanel(true));
        });
    } else {
      getOrderDetails();
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const readyImage = getAssetUrl('publishing-design-ready.png');
  return isLoading ? (
    <PublishingLoaderScreen customHeight={styles.setCustomHeight} loaderSize={LOADER_SIZE.LARGE} />
  ) : (
    <div className="">
      <div className={`flex-center spacing-p-t-6 spacing-p-b-4 flex-v-row ${styles.imageShadow}`}>
        <img className={canShare ? styles.imageHeight : styles.posterImageDesign} src={canShare ? readyImage : image} alt="" />
        {canShare ? <Text size={TextSize.LARGE} className="content-body spacing-p-t-4 spacing-p-b-5" val={window.i18next.t('pmwjs_ready_to_share')} /> : ''}
      </div>
      <div className="flex-center">
        {!isHighRes && !canShare ? (
          <div className="spacing-m-t-6 spacing-m-b-6">
            <ToggleSwitch
              className="spacing-m-r-2"
              id="example"
              isChecked={removeWatermark}
              onChange={() => {
                setRemoveWatermark(!removeWatermark);
              }}
            />

            <Text val="Remove Watermark" size={TextSize.XSMALL} bold />
          </div>
        ) : (
          ''
        )}
      </div>

      <div className="spacing-p-2">
        <Button isLoading={isLoading} onClick={canShare ? onShareToApp : initFooter} type={Type.PRIMARY} isFullWidth text={window.i18next.t('pmwjs_share_to_app')} />
      </div>
    </div>
  );
}
